import { cn } from '@/lib/utils';
import { Bot, Brain, BrainCircuit } from 'lucide-react';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { Kbd } from '@/components/Kbd';
import { Button } from '@/components/ui/button';
import { NotificationCountBadge } from '@/components/ui/notification-count-badge';
import { useSidebar } from '@/components/ui/sidebar';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { useHotkey } from '@/hooks/useHotkey';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    navigate?: (to: string) => void;
    showText?: boolean;
    tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
    unreadCount?: number;
    hotkey?: string;
}

const DMAgentsBtn = ({
    navigate,
    showText = true,
    tooltipSide = 'right',
    unreadCount = 0,
    hotkey,
    ...buttonProps
}: Props) => {
    const location = useLocation();
    const isActive = location.pathname.startsWith('/scheduled-jobs');
    const { isMobile, setOpenMobile } = useSidebar();

    const handleClick = () => {
        navigate?.('/scheduled-jobs');
        // Mirrors TimeGroupedThreadList's thread rows — picking a
        // destination from the mobile sidebar should close the off-canvas
        // drawer, not leave it open over the page it just navigated to.
        if (isMobile) setOpenMobile(false);
    };

    const hasUnread = unreadCount > 0;

    useHotkey(hotkey, handleClick);

    const button = (
        <Button
            variant="bare"
            id="agents-button"
            className={cn(
                'rounded-lg font-normal text-foreground transition-all duration-200',
                isActive && 'text-foreground font-medium',
                showText
                    ? 'group/nav-item justify-start gap-3 px-3 w-full h-10 text-base leading-6'
                    : 'justify-center w-full h-10 p-2'
            )}
            active={isActive}
            onClick={handleClick}
            {...buttonProps}
        >
            {showText ? (
                <BrainCircuit strokeWidth={1.5} className="size-5! shrink-0" />
            ) : (
                <span className="inline-flex relative">
                    <BrainCircuit
                        strokeWidth={1.5}
                        className="size-5! shrink-0"
                    />
                    {hasUnread && (
                        <NotificationCountBadge
                            count={unreadCount}
                            size="sm"
                            className="-top-2 -right-2 absolute"
                        />
                    )}
                </span>
            )}
            {showText && (
                <>
                    <span className="flex-1 min-w-0 text-left truncate">
                        Scheduled jobs
                    </span>
                    {hotkey && (
                        <Kbd className="hidden group-hover/nav-item:inline-flex shrink-0">
                            {hotkey}
                        </Kbd>
                    )}
                    {hasUnread && (
                        <NotificationCountBadge count={unreadCount} />
                    )}
                </>
            )}
        </Button>
    );

    const buttonContent = !showText ? (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side={tooltipSide}>
                    {hasUnread
                        ? `Scheduled jobs (${unreadCount} unread)`
                        : 'Scheduled jobs'}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    ) : (
        button
    );

    return <div className="flex justify-center">{buttonContent}</div>;
};

export default DMAgentsBtn;
